import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { Button, Page, TextLink, Wrap } from "../../components/Newsletter/theme";

/**
 * 뉴스레터 문자 수신거부 확인 페이지 (/u/:token).
 *
 * 문자 본문의 링크로 들어온다. 링크를 여는 것만으로는 아무것도 지우지 않고,
 * 버튼을 눌러야 /api/unsubscribe 로 POST 한다 — 문자앱·메신저가 미리보기를 만들려고
 * 링크를 먼저 열어 보기 때문이다. 토큰 검증과 삭제는 함수(functions/index.js)가 한다.
 *
 * 문자는 국내 번호로만 나가므로 한국어 화면 하나만 둔다.
 */

const CONTACT = "official@basakansoft.com";

const TEXT = {
  title: "뉴스레터 문자 수신거부",
  intro: "바삭한소프트 뉴스레터 문자를 더 이상 받지 않으시려면 아래 버튼을 눌러 주세요.",
  note: "등록된 전화번호는 바로 삭제됩니다. 이메일로도 구독하셨다면 이메일 구독은 그대로 유지됩니다.",
  submit: "수신거부",
  submitting: "처리 중",
  done: "수신거부되었습니다.",
  doneNote: "앞으로 뉴스레터 문자를 보내 드리지 않습니다. 그동안 받아 주셔서 감사합니다.",
  invalid: "링크가 올바르지 않습니다.",
  invalidNote: `문자에 있던 주소를 끝까지 그대로 열어 주세요. 계속 안 되면 ${CONTACT} 로 알려 주시면 바로 처리해 드립니다.`,
  error: "처리하지 못했습니다.",
  errorNote: `잠시 후 다시 시도해 주세요. 계속 안 되면 ${CONTACT} 로 알려 주시면 바로 처리해 드립니다.`,
  home: "홈으로",
};

function Unsubscribe() {
  const { token } = useParams();
  // confirm | submitting | done | invalid | error
  const [status, setStatus] = useState("confirm");

  useEffect(() => {
    document.title = `${TEXT.title} | 바삭한소프트`;
  }, []);

  const submit = async () => {
    setStatus("submitting");

    try {
      const response = await fetch("/api/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });

      if (response.status === 400) {
        setStatus("invalid");
        return;
      }

      const data = await response.json().catch(() => null);
      // "already" 도 완료로 보여 준다. 방문자 입장에선 이미 빠져 있다는 결과가 같다.
      setStatus(response.ok && data && data.ok ? "done" : "error");
    } catch (err) {
      console.error("[newsletter] 수신거부 요청 실패:", err);
      setStatus("error");
    }
  };

  const result = {
    done: [TEXT.done, TEXT.doneNote],
    invalid: [TEXT.invalid, TEXT.invalidNote],
    error: [TEXT.error, TEXT.errorNote],
  }[status];

  return (
    <Page>
      <Wrap>
        <Header>
          <Title>{TEXT.title}</Title>
        </Header>

        {result ? (
          <Section role="status">
            <ResultTitle>{result[0]}</ResultTitle>
            <Note>{result[1]}</Note>
            {status === "error" ? (
              <Actions>
                <Button type="button" onClick={submit}>
                  {TEXT.submit}
                </Button>
              </Actions>
            ) : (
              <Actions>
                <TextLink as={Link} to="/ko">
                  {TEXT.home}
                </TextLink>
              </Actions>
            )}
          </Section>
        ) : (
          <Section>
            <Note>{TEXT.intro}</Note>
            <Note>{TEXT.note}</Note>
            <Actions>
              <Button type="button" onClick={submit} disabled={status === "submitting"}>
                {status === "submitting" ? TEXT.submitting : TEXT.submit}
              </Button>
            </Actions>
          </Section>
        )}
      </Wrap>
    </Page>
  );
}

const Header = styled.header`
  padding: 5rem 0 1.5rem;

  @media screen and (max-width: 768px) {
    padding: 3rem 0 1rem;
  }
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 800;
  line-height: 1.3;
  letter-spacing: -0.035em;
  color: var(--nl-ink);

  @media screen and (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Section = styled.section`
  padding-top: 1.5rem;
  border-top: 1px solid var(--nl-line);
`;

const ResultTitle = styled.p`
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--nl-ink);
`;

const Note = styled.p`
  max-width: 30rem;
  margin-top: 0.5rem;
  font-size: 0.9375rem;
  line-height: 1.75;
  color: var(--nl-ink-soft);
`;

const Actions = styled.div`
  margin-top: 1.75rem;
`;

export default Unsubscribe;
